if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.submitButtonId = this.dataset.submitButtonId;
        this.productFormId = this.dataset.productFormId;

        this.handleClick = this.handleClick.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.handleIntersect = this.handleIntersect.bind(this);
        this.handleMediaChange = this.handleMediaChange.bind(this);
      }

      connectedCallback() {
        /*
         * Verplaats de sticky bar rechtstreeks naar body.
         * Hierdoor kan een parent met transform of overflow
         * de fixed position niet blokkeren.
         */
        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
          return;
        }

        /*
         * Voorkomt dubbele event listeners wanneer Shopify
         * de section opnieuw rendert.
         */
        if (this.initialized) return;
        this.initialized = true;

        this.button = this.querySelector(
          '.sticky-add-to-cart__button'
        );

        this.variantSelect = this.querySelector(
          '.sticky-add-to-cart__variant-select'
        );

        this.priceWrapper = this.querySelector(
          `#StickyAddToCartPrice-${this.sectionId}`
        );

        this.realButton = document.getElementById(
          this.submitButtonId
        );

        if (!this.button || !this.realButton) return;

        this.mediaQuery = window.matchMedia(
          '(max-width: 749px)'
        );

        this.button.addEventListener(
          'click',
          this.handleClick
        );

        if (this.variantSelect) {
          this.variantSelect.addEventListener(
            'change',
            this.handleVariantChange
          );
        }

        this.observer = new IntersectionObserver(
          this.handleIntersect,
          {
            rootMargin: '0px 0px -10% 0px',
            threshold: 0,
          }
        );

        this.observer.observe(this.realButton);

        if (this.mediaQuery.addEventListener) {
          this.mediaQuery.addEventListener(
            'change',
            this.handleMediaChange
          );
        } else {
          this.mediaQuery.addListener(
            this.handleMediaChange
          );
        }

        /*
         * Synchronisatie met Dawn wanneer iemand bovenaan
         * de productpagina een andere maat selecteert.
         */
        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined'
        ) {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            (event) => {
              if (
                !event ||
                !event.data ||
                event.data.sectionId !== this.sectionId
              ) {
                return;
              }

              this.updateFromVariant(
                event.data.html,
                event.data.variant
              );
            }
          );
        }

        this.syncSelectWithProductForm();
      }

      disconnectedCallback() {
        if (!this.initialized) return;

        this.observer?.disconnect();

        this.button?.removeEventListener(
          'click',
          this.handleClick
        );

        this.variantSelect?.removeEventListener(
          'change',
          this.handleVariantChange
        );

        if (this.mediaQuery?.removeEventListener) {
          this.mediaQuery.removeEventListener(
            'change',
            this.handleMediaChange
          );
        } else {
          this.mediaQuery?.removeListener(
            this.handleMediaChange
          );
        }

        if (
          typeof this.variantChangeUnsubscriber === 'function'
        ) {
          this.variantChangeUnsubscriber();
        }

        this.initialized = false;
      }

      handleMediaChange(event) {
        if (!event.matches) {
          this.classList.remove('is-visible');
        }
      }

      handleIntersect(entries) {
        const entry = entries[entries.length - 1];

        const shouldShow =
          !entry.isIntersecting &&
          this.mediaQuery.matches;

        this.classList.toggle(
          'is-visible',
          shouldShow
        );
      }

      getRealButton() {
        return document.getElementById(
          this.submitButtonId
        );
      }

      getProductForm() {
        const formById = this.productFormId
          ? document.getElementById(this.productFormId)
          : null;

        if (formById) return formById;

        const realButton = this.getRealButton();

        if (realButton) {
          const closestForm = realButton.closest('form');

          if (closestForm) return closestForm;
        }

        return document.querySelector(
          `form[id*="product-form-${this.sectionId}"]`
        );
      }

      getVariantInput() {
        const productForm = this.getProductForm();

        if (!productForm) return null;

        return productForm.querySelector(
          'input[name="id"]'
        );
      }

      syncSelectWithProductForm() {
        if (!this.variantSelect) return;

        const variantInput = this.getVariantInput();

        if (!variantInput || !variantInput.value) return;

        const matchingOption = Array.from(
          this.variantSelect.options
        ).find(
          (option) =>
            option.value === variantInput.value
        );

        if (matchingOption) {
          this.variantSelect.value =
            variantInput.value;

          this.updateButton(
            matchingOption.dataset.available === 'true'
          );
        }
      }

      handleVariantChange(event) {
        const select = event.currentTarget;

        const selectedOption =
          select.options[select.selectedIndex];

        if (!selectedOption) return;

        const variantId = selectedOption.value;

        const isAvailable =
          selectedOption.dataset.available === 'true';

        const variantOptions = this.parseVariantOptions(
          selectedOption.dataset.options
        );

        /*
         * Zet onmiddellijk de juiste variant-ID in het echte
         * Shopify-productformulier.
         */
        this.setVariantInput(variantId);

        /*
         * Synchroniseer de gewone maatselector bovenaan.
         * Zo blijven beide selectors gelijk.
         */
        this.syncMainVariantPicker(variantOptions);

        this.updateButton(isAvailable);
        this.updatePrice(selectedOption);
      }

      parseVariantOptions(optionsValue) {
        if (!optionsValue) return [];

        try {
          return JSON.parse(optionsValue);
        } catch (error) {
          console.warn(
            'Sticky Add to Cart: variant options konden niet worden gelezen.',
            error
          );

          return [];
        }
      }

      setVariantInput(variantId) {
        const variantInput = this.getVariantInput();

        if (!variantInput) return;

        variantInput.value = variantId;

        variantInput.dispatchEvent(
          new Event('input', {
            bubbles: true,
          })
        );

        variantInput.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );
      }

      getMainVariantPicker() {
        const selectors = [
          `variant-selects[data-section="${this.sectionId}"]`,
          `variant-radios[data-section="${this.sectionId}"]`,
          `#variant-selects-${this.sectionId}`,
          `#variant-radios-${this.sectionId}`,
        ];

        for (const selector of selectors) {
          const picker = document.querySelector(selector);

          if (picker && !this.contains(picker)) {
            return picker;
          }
        }

        return null;
      }

      syncMainVariantPicker(variantOptions) {
        if (
          !Array.isArray(variantOptions) ||
          variantOptions.length === 0
        ) {
          return;
        }

        const picker = this.getMainVariantPicker();

        if (!picker) return;

        const fieldsets = Array.from(
          picker.querySelectorAll('fieldset')
        );

        const selects = Array.from(
          picker.querySelectorAll('select')
        );

        let finalControl = null;

        variantOptions.forEach((optionValue, index) => {
          const fieldset = fieldsets[index];

          if (fieldset) {
            const radios = Array.from(
              fieldset.querySelectorAll(
                'input[type="radio"]'
              )
            );

            const matchingRadio = radios.find(
              (radio) => radio.value === optionValue
            );

            if (matchingRadio) {
              matchingRadio.checked = true;
              finalControl = matchingRadio;
              return;
            }
          }

          const select = selects[index];

          if (select) {
            const matchingOption = Array.from(
              select.options
            ).find(
              (option) =>
                option.value === optionValue
            );

            if (matchingOption) {
              select.value = optionValue;
              finalControl = select;
            }
          }
        });

        /*
         * Eén change-event is voldoende om Dawn de nieuwe
         * variant te laten verwerken.
         */
        if (finalControl) {
          finalControl.dispatchEvent(
            new Event('change', {
              bubbles: true,
            })
          );
        }
      }

      updateButton(isAvailable) {
        if (!this.button) return;

        this.button.toggleAttribute(
          'disabled',
          !isAvailable
        );

        const label = this.button.querySelector('span');

        if (!label) return;

        if (isAvailable) {
          label.textContent =
            this.button.dataset.addToCartText ||
            'Add to cart';
        } else {
          label.textContent =
            this.button.dataset.soldOutText ||
            'Sold out';
        }
      }

      updatePrice(selectedOption) {
        if (!this.priceWrapper) return;

        const price =
          selectedOption.dataset.price;

        const comparePrice =
          selectedOption.dataset.comparePrice;

        if (!price) return;

        const hasComparePrice =
          comparePrice &&
          comparePrice.trim() !== '' &&
          comparePrice !== price;

        if (hasComparePrice) {
          this.priceWrapper.innerHTML = `
            <div class="price price--small price--on-sale">
              <div class="price__container">
                <div class="price__sale">
                  <s class="price-item price-item--regular">
                    ${comparePrice}
                  </s>

                  <span class="price-item price-item--sale price-item--last">
                    ${price}
                  </span>
                </div>
              </div>
            </div>
          `;
        } else {
          this.priceWrapper.innerHTML = `
            <div class="price price--small">
              <div class="price__container">
                <div class="price__regular">
                  <span class="price-item price-item--regular">
                    ${price}
                  </span>
                </div>
              </div>
            </div>
          `;
        }
      }

      updateFromVariant(html, variant) {
        if (variant && this.variantSelect) {
          const variantId = String(variant.id);

          const matchingOption = Array.from(
            this.variantSelect.options
          ).find(
            (option) => option.value === variantId
          );

          if (matchingOption) {
            this.variantSelect.value = variantId;
          }
        }

        if (!html) return;

        const newRealButton = html.getElementById(
          this.submitButtonId
        );

        if (newRealButton && this.button) {
          const isDisabled =
            newRealButton.hasAttribute('disabled');

          this.button.toggleAttribute(
            'disabled',
            isDisabled
          );

          const newLabel = newRealButton
            .querySelector('span')
            ?.textContent
            ?.trim();

          const currentLabel =
            this.button.querySelector('span');

          if (currentLabel && newLabel) {
            currentLabel.textContent = newLabel;
          }
        }

        const newPrice = html.getElementById(
          `StickyAddToCartPrice-${this.sectionId}`
        );

        if (newPrice && this.priceWrapper) {
          this.priceWrapper.innerHTML =
            newPrice.innerHTML;
        }
      }

      handleClick(event) {
        event.preventDefault();

        if (
          !this.button ||
          this.button.hasAttribute('disabled')
        ) {
          return;
        }

        /*
         * Controleer nogmaals dat de gekozen sticky variant
         * in het echte productformulier staat.
         */
        if (this.variantSelect?.value) {
          this.setVariantInput(
            this.variantSelect.value
          );
        }

        const realButton = this.getRealButton();

        if (realButton) {
          realButton.click();
        }
      }
    }
  );
}